import React, { useState, useRef, useEffect } from "react";

/** Column header input with local draft state — commits only on blur or Enter */
function ColHeaderInput({ col, idx, onRename, onRemove, canRemove, language }) {
    const [draft, setDraft] = useState(col);
    // Keep draft in sync when the column name changes externally
    useEffect(() => {
        setDraft(col);
    }, [col]);
    const commit = () => onRename(col, draft);
    return (
        <>
            <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        commit();
                        e.target.blur();
                    }
                }}
                className="w-full bg-transparent text-sm font-semibold text-gray-700 focus:outline-none placeholder-gray-400"
                placeholder={`Column ${idx + 1}`}
            />
            {canRemove && (
                <button
                    type="button"
                    className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                    onClick={onRemove}
                    title={language === "zh" ? "刪除欄位" : "Remove column"}
                >
                    <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            )}
        </>
    );
}

/**
 * Controlled TableType component.
 *
 * Data shape (flat array of row-objects):
 *   data – Array<{ [colName]: string }>
 *
 *   e.g. thermal conductivity table:
 *   [
 *     { attribute: "thermal conductivity", value: "5", unit: "wm/k", test_method: "" }
 *   ]
 *
 *   e.g. diameter table (multiple rows for multiple values):
 *   [
 *     { diameter: "Ø4", "thickness(mm)": "2",   "width(mm)": "5.65" },
 *     { diameter: "Ø4", "thickness(mm)": "2.5", "width(mm)": "5.55" },
 *     { diameter: "Ø4", "thickness(mm)": "3",   "width(mm)": "5.45" },
 *   ]
 *
 * Column order is tracked separately so it doesn't depend on object key order.
 *
 * Props:
 *   attribute  – { type: "table", attributeName: string, data: Array<{[col]: string}>, notes?: string[] }
 *   onChange   – (updatedAttribute) => void
 *   onDelete   – () => void
 *   language   – "en" | "zh"
 */
export const TableType = ({
    attribute,
    onChange,
    onDelete,
    language = "en",
}) => {
    // ── Local UI state ───────────────────────────────────────────────────────
    const [newColName, setNewColName] = useState("");
    const [showColInput, setShowColInput] = useState(false);
    const [deleteRowIdx, setDeleteRowIdx] = useState(null); // null = closed
    const [dragRowIdx, setDragRowIdx] = useState(null);
    const [dragOverRowIdx, setDragOverRowIdx] = useState(null);
    const colInputRef = useRef(null);

    // Stable row IDs so React never reuses the wrong DOM nodes when rows
    // are added / removed / reordered.
    const rowIds = useRef([]);
    const rows = Array.isArray(attribute.data) ? attribute.data : [];
    // Grow / shrink the ids array to match current row count.
    while (rowIds.current.length < rows.length) {
        rowIds.current.push(
            `row-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        );
    }
    rowIds.current = rowIds.current.slice(0, rows.length);

    useEffect(() => {
        if (showColInput && colInputRef.current) {
            colInputRef.current.focus();
        }
    }, [showColInput]);

    // ── Data helpers ─────────────────────────────────────────────────────────
    const updateName = (value) => {
        onChange({ ...attribute, attributeName: value });
    };

    // data is a flat array of row-objects: Array<{ [col]: string }>
    // (rows is already declared above, next to rowIds)

    // Derive ordered column list as a union of all keys across all rows,
    // preserving the order they first appear (handles sparse competitor data).
    const columns =
        rows.length > 0
            ? [...new Set(rows.flatMap((row) => Object.keys(row)))]
            : [];

    const updateRows = (newRows) => {
        onChange({ ...attribute, data: newRows });
    };

    // ── Notes helpers ────────────────────────────────────────────────────────
    const notes = Array.isArray(attribute.notes) ? attribute.notes : [];
    const updateNotes = (newNotes) => {
        onChange({ ...attribute, notes: newNotes });
    };
    const addNote = () => updateNotes([...notes, ""]);
    const updateNote = (idx, value) => {
        updateNotes(notes.map((n, i) => (i === idx ? value : n)));
    };
    const removeNote = (idx) => updateNotes(notes.filter((_, i) => i !== idx));

    // ── Column helpers ──────────────────────────────────────────────────────
    const confirmAddColumn = () => {
        const name = newColName.trim();
        if (!name || columns.includes(name)) return;

        let newRows;
        if (rows.length === 0) {
            // No rows yet — seed one empty row so columns can be derived
            newRows = [{ [name]: "" }];
        } else {
            // Add the new key to every existing row
            newRows = rows.map((row) => ({ ...row, [name]: "" }));
        }

        updateRows(newRows);
        setNewColName("");
        setShowColInput(false);
    };

    const removeColumn = (colName) => {
        const newRows = rows.map((row) => {
            const { [colName]: _, ...rest } = row;
            return rest;
        });
        // If no columns remain across any row, clear all rows
        const remainingCols = new Set(newRows.flatMap((r) => Object.keys(r)));
        updateRows(remainingCols.size === 0 ? [] : newRows);
    };

    const updateColumnName = (oldName, newName) => {
        const trimmed = newName.trim();
        if (!trimmed || trimmed === oldName || columns.includes(trimmed))
            return;
        const newRows = rows.map((row) => {
            // Rebuild the object preserving key order
            const rebuilt = {};
            for (const key of Object.keys(row)) {
                rebuilt[key === oldName ? trimmed : key] = row[key];
            }
            return rebuilt;
        });
        updateRows(newRows);
    };

    const moveColumn = (idx, direction) => {
        const targetIdx = idx + direction;
        if (targetIdx < 0 || targetIdx >= columns.length) return;
        // Swap the two column positions in every row by rebuilding key order
        const newCols = [...columns];
        [newCols[idx], newCols[targetIdx]] = [newCols[targetIdx], newCols[idx]];
        const newRows = rows.map((row) => {
            const rebuilt = {};
            for (const key of newCols) {
                if (key in row) rebuilt[key] = row[key];
            }
            // Preserve any keys not in columns (shouldn't happen, but be safe)
            for (const key of Object.keys(row)) {
                if (!(key in rebuilt)) rebuilt[key] = row[key];
            }
            return rebuilt;
        });
        updateRows(newRows);
    };

    // ── Row helpers ─────────────────────────────────────────────────────────
    const addRow = () => {
        const emptyRow = {};
        columns.forEach((col) => {
            emptyRow[col] = "";
        });
        updateRows([...rows, emptyRow]);
    };

    const confirmRemoveRow = () => {
        if (deleteRowIdx === null) return;
        rowIds.current.splice(deleteRowIdx, 1);
        updateRows(rows.filter((_, i) => i !== deleteRowIdx));
        setDeleteRowIdx(null);
    };

    // ── Row drag-and-drop handlers ───────────────────────────────────────────
    const handleRowDragStart = (e, idx) => {
        setDragRowIdx(idx);
        e.dataTransfer.effectAllowed = "move";
        // Transparent 1×1 ghost so the browser default image doesn't flash
        const ghost = document.createElement("div");
        ghost.style.cssText = "position:absolute;top:-9999px;opacity:0;";
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 0, 0);
        setTimeout(() => document.body.removeChild(ghost), 0);
    };
    const handleRowDragOver = (e, idx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (idx !== dragRowIdx) setDragOverRowIdx(idx);
    };
    const handleRowDrop = (e, idx) => {
        e.preventDefault();
        if (dragRowIdx === null || dragRowIdx === idx) {
            setDragRowIdx(null);
            setDragOverRowIdx(null);
            return;
        }
        const newRows = [...rows];
        const newIds = [...rowIds.current];
        const [movedRow] = newRows.splice(dragRowIdx, 1);
        const [movedId] = newIds.splice(dragRowIdx, 1);
        newRows.splice(idx, 0, movedRow);
        newIds.splice(idx, 0, movedId);
        rowIds.current = newIds;
        updateRows(newRows);
        setDragRowIdx(null);
        setDragOverRowIdx(null);
    };
    const handleRowDragEnd = () => {
        setDragRowIdx(null);
        setDragOverRowIdx(null);
    };

    // ── Cell helper ──────────────────────────────────────────────────────────
    const updateCell = (rowIdx, col, value) => {
        const newRows = rows.map((row, i) =>
            i === rowIdx ? { ...row, [col]: value } : row,
        );
        updateRows(newRows);
    };

    // ── Grid layout ──────────────────────────────────────────────────────────
    // [col0..n minmax(120px,1fr)] [delete+add-col 40px]
    const gridTemplateColumns =
        columns.length === 0
            ? "32px 32px"
            : `32px repeat(${columns.length}, minmax(120px, 1fr)) 32px`;

    return (
        <>
            {/* ── Delete row confirmation modal ── */}
            {deleteRowIdx !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setDeleteRowIdx(null)}
                    />
                    {/* Dialog */}
                    <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80 flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <svg
                                    className="w-5 h-5 text-red-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                    />
                                </svg>
                            </div>
                            <div>
                                <p className="font-semibold text-gray-800">
                                    {language === "zh"
                                        ? "確認刪除列？"
                                        : "Delete this row?"}
                                </p>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {language === "zh"
                                        ? "此操作無法復原。"
                                        : "This action cannot be undone."}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                className="px-4 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-50 transition-colors"
                                onClick={() => setDeleteRowIdx(null)}
                            >
                                {language === "zh" ? "取消" : "Cancel"}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-1.5 rounded-lg bg-red-500 text-white text-sm hover:bg-red-600 transition-colors"
                                onClick={confirmRemoveRow}
                            >
                                {language === "zh" ? "刪除" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="rounded-lg border border-gray-200 overflow-hidden">
                {/* ── Title bar ── */}
                <div className="bg-[#3C3C3C] px-4 py-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-purple-200 text-purple-700 shrink-0">
                            {language === "zh" ? "表格" : "Table"}
                        </span>
                        <input
                            type="text"
                            value={attribute.attributeName}
                            placeholder={
                                language === "zh"
                                    ? "屬性名稱"
                                    : "Attribute name"
                            }
                            className="flex-1 bg-transparent border-0 border-b-2 border-gray-200 focus:border-white px-1 py-0.5 text-sm font-medium text-white focus:outline-none placeholder-gray-400 transition-colors"
                            onChange={(e) => updateName(e.target.value)}
                        />
                    </div>
                    <button
                        type="button"
                        className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors shrink-0"
                        onClick={onDelete}
                        title={
                            language === "zh" ? "刪除屬性" : "Delete attribute"
                        }
                    >
                        <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>

                {/* ── Table body ── */}
                <div className="bg-white px-4 py-3">
                    <div className="overflow-x-auto">
                        <div
                            className="grid gap-0"
                            style={{ gridTemplateColumns }}
                        >
                            {/* ── Column move-arrow row ── */}
                            {/* leading spacer (drag column) */}
                            {columns.length > 0 && (
                                <div className="border border-gray-100 bg-blue-50" />
                            )}
                            {columns.length > 0 &&
                                columns.map((col, idx) => (
                                    <div
                                        key={`move-${col}`}
                                        className="border border-gray-100 bg-blue-50 flex items-center justify-center gap-1 py-1"
                                    >
                                        <button
                                            type="button"
                                            disabled={idx === 0}
                                            onClick={() => moveColumn(idx, -1)}
                                            title={
                                                language === "zh"
                                                    ? "左移欄位"
                                                    : "Move column left"
                                            }
                                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-blue-500 hover:bg-blue-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                                        >
                                            <svg
                                                className="w-3.5 h-3.5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2.5}
                                                    d="M15 19l-7-7 7-7"
                                                />
                                            </svg>
                                            {/* {language === "zh"
                                                ? "左移"
                                                : "Left"} */}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={
                                                idx === columns.length - 1
                                            }
                                            onClick={() => moveColumn(idx, 1)}
                                            title={
                                                language === "zh"
                                                    ? "右移欄位"
                                                    : "Move column right"
                                            }
                                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-blue-500 hover:bg-blue-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                                        >
                                            {/* {language === "zh"
                                                ? "右移"
                                                : "Right"} */}
                                            <svg
                                                className="w-3.5 h-3.5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2.5}
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            {/* spacer cell for the +/delete column */}
                            {columns.length > 0 && (
                                <div className="border border-gray-100 bg-blue-50" />
                            )}

                            {/* ── Header row ── */}
                            {/* leading spacer (drag column) */}
                            {columns.length > 0 && (
                                <div className="border border-gray-200 bg-gray-50" />
                            )}
                            {columns.map((col, idx) => (
                                <div
                                    key={`header-${col}`}
                                    className="border border-gray-200 bg-gray-50 px-2 py-1.5 flex items-center gap-1"
                                >
                                    <ColHeaderInput
                                        col={col}
                                        idx={idx}
                                        onRename={updateColumnName}
                                        onRemove={() => removeColumn(col)}
                                        canRemove={columns.length > 1}
                                        language={language}
                                    />
                                </div>
                            ))}

                            {/* ── Add column button — last cell of header row (action column) ── */}
                            <div className="border border-gray-200 bg-gray-50 flex items-center justify-center">
                                <button
                                    type="button"
                                    className="w-full h-full flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors font-bold text-lg"
                                    onClick={() => setShowColInput((v) => !v)}
                                    title={
                                        language === "zh"
                                            ? "新增欄位"
                                            : "Add column"
                                    }
                                >
                                    {showColInput ? "−" : "+"}
                                </button>
                            </div>

                            {/* ── Data rows ── */}
                            {rows.map((row, rowIdx) => {
                                const isDragging = dragRowIdx === rowIdx;
                                const isDropTarget =
                                    dragOverRowIdx === rowIdx &&
                                    dragRowIdx !== rowIdx;
                                const isAbove =
                                    dragRowIdx !== null &&
                                    dragOverRowIdx === rowIdx &&
                                    dragRowIdx > rowIdx;
                                return (
                                    <React.Fragment
                                        key={rowIds.current[rowIdx]}
                                    >
                                        {/* ── Drag handle cell (leftmost column) ── */}
                                        <div
                                            className={`border border-gray-200 flex items-center justify-center transition-colors ${
                                                isDragging
                                                    ? "opacity-40 bg-blue-50"
                                                    : isDropTarget
                                                      ? isAbove
                                                          ? "border-t-2 border-t-blue-400"
                                                          : "border-b-2 border-b-blue-400"
                                                      : ""
                                            }`}
                                            onDragOver={(e) =>
                                                handleRowDragOver(e, rowIdx)
                                            }
                                            onDrop={(e) =>
                                                handleRowDrop(e, rowIdx)
                                            }
                                        >
                                            <div
                                                draggable
                                                onDragStart={(e) =>
                                                    handleRowDragStart(
                                                        e,
                                                        rowIdx,
                                                    )
                                                }
                                                onDragEnd={handleRowDragEnd}
                                                title={
                                                    language === "zh"
                                                        ? "拖曳排序"
                                                        : "Drag to reorder"
                                                }
                                                className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-blue-400 transition-colors"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path d="M7 4a1 1 0 100 2 1 1 0 000-2zM13 4a1 1 0 100 2 1 1 0 000-2zM7 9a1 1 0 100 2 1 1 0 000-2zM13 9a1 1 0 100 2 1 1 0 000-2zM7 14a1 1 0 100 2 1 1 0 000-2zM13 14a1 1 0 100 2 1 1 0 000-2z" />
                                                </svg>
                                            </div>
                                        </div>

                                        {columns.map((col, colIdx) => (
                                            <div
                                                key={`cell-${rowIdx}-${colIdx}`}
                                                className={`border border-gray-200 px-2 py-1 align-top transition-colors ${
                                                    isDragging
                                                        ? "opacity-40 bg-blue-50"
                                                        : isDropTarget
                                                          ? isAbove
                                                              ? "border-t-2 border-t-blue-400"
                                                              : "border-b-2 border-b-blue-400"
                                                          : ""
                                                }`}
                                            >
                                                <input
                                                    type="text"
                                                    value={row[col] ?? ""}
                                                    onChange={(e) =>
                                                        updateCell(
                                                            rowIdx,
                                                            col,
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder={col}
                                                    className="w-full bg-transparent border-0 border-b border-transparent focus:border-blue-400 px-1 py-0.5 text-sm text-gray-800 focus:outline-none placeholder-gray-300 transition-colors"
                                                />
                                            </div>
                                        ))}

                                        {/* ── Delete cell (rightmost column) ── */}
                                        <div
                                            className={`border border-gray-200 flex items-center justify-center transition-colors ${
                                                isDragging
                                                    ? "opacity-40 bg-blue-50"
                                                    : isDropTarget
                                                      ? isAbove
                                                          ? "border-t-2 border-t-blue-400"
                                                          : "border-b-2 border-b-blue-400"
                                                      : ""
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                className="text-gray-300 hover:text-red-500 transition-colors"
                                                onClick={() =>
                                                    setDeleteRowIdx(rowIdx)
                                                }
                                                title={
                                                    language === "zh"
                                                        ? "刪除列"
                                                        : "Remove row"
                                                }
                                            >
                                                <svg
                                                    className="w-3.5 h-3.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* ── Add column inline form (shown below the table) ── */}
                        {showColInput && (
                            <div className="mt-2 flex items-center gap-2 px-3 py-2 border border-blue-300 rounded-lg bg-blue-50">
                                <span className="text-xs font-medium text-blue-600 shrink-0">
                                    {language === "zh"
                                        ? "新欄位名稱："
                                        : "New column name:"}
                                </span>
                                <input
                                    ref={colInputRef}
                                    type="text"
                                    value={newColName}
                                    onChange={(e) =>
                                        setNewColName(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                            confirmAddColumn();
                                        if (e.key === "Escape") {
                                            setShowColInput(false);
                                            setNewColName("");
                                        }
                                    }}
                                    placeholder={
                                        language === "zh"
                                            ? "輸入欄位名稱…"
                                            : "Enter column name…"
                                    }
                                    className="flex-1 bg-white border border-blue-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
                                />
                                <button
                                    type="button"
                                    onClick={confirmAddColumn}
                                    disabled={!newColName.trim()}
                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:opacity-40 transition-colors shrink-0"
                                >
                                    {language === "zh" ? "確認" : "Add"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowColInput(false);
                                        setNewColName("");
                                    }}
                                    className="px-3 py-1 border border-gray-300 text-gray-500 text-sm rounded hover:bg-gray-100 transition-colors shrink-0"
                                >
                                    {language === "zh" ? "取消" : "Cancel"}
                                </button>
                            </div>
                        )}

                        {/* ── Add row button ── */}
                        {columns.length > 0 && (
                            <button
                                type="button"
                                className="mt-2 w-full py-1.5 border-2 border-dashed border-gray-300 text-gray-500 rounded hover:border-blue-400 hover:text-blue-500 transition-colors text-sm font-medium"
                                onClick={addRow}
                            >
                                {language === "zh" ? "+ 新增列" : "+ Add row"}
                            </button>
                        )}

                        {columns.length === 0 && (
                            <div className="text-center text-gray-400 text-sm py-6">
                                {language === "zh"
                                    ? "點擊右側「+」開始建立表格"
                                    : 'Click the "+" to start building your table'}
                            </div>
                        )}

                        {/* ── Notes section ── */}
                        <div className="my-3 border border-gray-200 rounded-lg overflow-hidden">
                            {/* Notes header / toggle */}

                            <div className="px-3 py-2 flex flex-col gap-1.5 bg-white">
                                {notes.length === 0 && (
                                    <p className="text-xs text-gray-400 py-1">
                                        {language === "zh"
                                            ? "尚無備註"
                                            : "No notes yet"}
                                    </p>
                                )}
                                {notes.map((note, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-start gap-2"
                                    >
                                        <span className="mt-1.5 text-xs text-gray-400 shrink-0 w-4 text-right">
                                            {idx + 1}.
                                        </span>
                                        <input
                                            type="text"
                                            value={note}
                                            onChange={(e) =>
                                                updateNote(idx, e.target.value)
                                            }
                                            placeholder={
                                                language === "zh"
                                                    ? "輸入備註…"
                                                    : "Enter note…"
                                            }
                                            className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs text-gray-700 focus:outline-none focus:border-blue-400 transition-colors"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeNote(idx)}
                                            className="mt-1 text-gray-300 hover:text-red-500 transition-colors shrink-0"
                                            title={
                                                language === "zh"
                                                    ? "刪除備註"
                                                    : "Remove note"
                                            }
                                        >
                                            <svg
                                                className="w-3.5 h-3.5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addNote}
                                    className="mt-1 self-start text-xs text-blue-500 hover:text-blue-600 font-medium transition-colors"
                                >
                                    {language === "zh"
                                        ? "+ 新增備註"
                                        : "+ Add note"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
